import React from 'react';
import { NavLink } from 'react-router-dom';
import { FiCheck, FiSettings } from 'react-icons/fi';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { Icon } from '../ui/Icon';

type CookiePrefs = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_KEY = 'lbf_cookie_prefs_v1';

export function CookieBanner() {
  const [prefs, setPrefs] = useLocalStorageState<CookiePrefs | null>(COOKIE_KEY, null);
  const [expanded, setExpanded] = React.useState(false);
  const [draft, setDraft] = React.useState<CookiePrefs>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  // Wait for age gate to be validated before asking cookies (avoid stacking modals).
  const [ageState] = useLocalStorageState<'unknown' | 'yes' | 'no'>('lbf_age_state', 'unknown');
  if (ageState !== 'yes') return null;

  if (prefs) return null;

  const commit = (next: CookiePrefs) => setPrefs(next);

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] p-4">
      <div className="lbf-container">
        <div className="lbf-card lbf-glow p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-2xl">
              <div className="text-sm font-bold text-white">Cookies</div>
              <p className="mt-1 text-sm text-zinc-200/85">
                On utilise des cookies essentiels pour le fonctionnement. Les autres restent optionnels. Tu gardes la
                main.
              </p>
              <div className="mt-2 text-xs text-zinc-400/90">
                Détails: <NavLink to="/politique-cookies">politique cookies</NavLink>.
              </div>

              {expanded ? (
                <div className="mt-4 grid gap-2 text-sm">
                  <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                    <input className="mt-1" type="checkbox" checked readOnly />
                    <div>
                      <div className="font-semibold text-white/90">Essentiels (obligatoires)</div>
                      <div className="text-xs text-zinc-300/80">Sécurité, préférences de base, affichage.</div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                    <input
                      className="mt-1"
                      type="checkbox"
                      checked={draft.analytics}
                      onChange={(e) => setDraft((p) => ({ ...p, analytics: e.target.checked }))}
                    />
                    <div>
                      <div className="font-semibold text-white/90">Mesure d’audience (optionnel)</div>
                      <div className="text-xs text-zinc-300/80">Nous aide à améliorer les pages et la vitesse.</div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                    <input
                      className="mt-1"
                      type="checkbox"
                      checked={draft.marketing}
                      onChange={(e) => setDraft((p) => ({ ...p, marketing: e.target.checked }))}
                    />
                    <div>
                      <div className="font-semibold text-white/90">Marketing (désactivé par défaut)</div>
                      <div className="text-xs text-zinc-300/80">On le laisse off tant que tu ne l’actives pas.</div>
                    </div>
                  </label>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2 sm:min-w-[220px] sm:items-stretch">
              <button
                type="button"
                className="lbf-btn-primary"
                onClick={() => commit({ essential: true, analytics: true, marketing: false })}
              >
                <Icon icon={FiCheck} aria-hidden="true" /> Accepter
              </button>
              <button
                type="button"
                className="lbf-btn-ghost"
                onClick={() => commit({ essential: true, analytics: false, marketing: false })}
              >
                Refuser (hors essentiels)
              </button>
              {expanded ? (
                <button type="button" className="lbf-btn-ghost" onClick={() => commit(draft)}>
                  Enregistrer
                </button>
              ) : (
                <button type="button" className="lbf-btn-ghost" onClick={() => setExpanded(true)}>
                  <Icon icon={FiSettings} aria-hidden="true" /> Personnaliser
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


